export interface AuthenticatedUser {
  id: string;
  walletAddress: string;
  role: string;
  username: string | null;
  email: string | null;
  avatar?: string | null;
  pointsBalance: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AvatarUploadUrlResponse {
  imageId: string;
  uploadURL: string;
}

export interface AvatarResponse {
  avatar: string;
}

export interface UpdateUserRequest {
  email: string;
  username: string;
}

export interface UserEnvelope {
  user: AuthenticatedUser;
}

export interface EmailVerificationResponse {
  message: "Email verified successfully";
}

const extractErrorMessage = async (response: Response, fallbackMessage: string) => {
  const text = await response.text();

  if (!text) return fallbackMessage;

  try {
    const body = JSON.parse(text) as { message?: unknown; error?: unknown };
    const message = body.message || body.error;

    if (typeof message === "string" && message.trim()) {
      return message;
    }

    if (Array.isArray(message)) {
      const firstMessage = message.find((item) => typeof item === "string" && item.trim());
      if (firstMessage) return firstMessage;
    }
  } catch {
    if (text.trim()) return text;
  }

  return fallbackMessage;
};

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("tokenUser");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

const getBaseUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const updateUserIdentity = async (
  userId: string,
  payload: UpdateUserRequest
): Promise<UserEnvelope> => {
  const base = getBaseUrl();
  const response = await fetch(`${base}/users/${userId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(
      response,
      "Profile update failed. Check your email, username, or try another username."
    ));
  }

  return response.json();
};

export const verifyEmailToken = async (token: string): Promise<EmailVerificationResponse> => {
  const normalizedToken = token.trim();

  if (!normalizedToken) {
    throw new Error("Verification token is required.");
  }

  const base = getBaseUrl();
  const response = await fetch(`${base}/verify?token=${encodeURIComponent(normalizedToken)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(
      response,
      "Email verification failed. Please request a new verification email."
    ));
  }

  return response.json();
};

export const createAvatarUploadUrl = async (): Promise<AvatarUploadUrlResponse> => {
  const response = await fetch(`${getBaseUrl()}/users/me/avatar-upload-url`, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, "Unable to prepare the avatar upload."));
  }

  return response.json();
};

export const uploadAvatarToCloudflare = async (file: Blob, uploadURL: string): Promise<void> => {
  const form = new FormData();
  form.append("file", file, "avatar.jpg");
  const response = await fetch(uploadURL, { method: "POST", body: form });

  if (!response.ok) {
    throw new Error("Unable to upload the avatar image.");
  }
};

export const completeAvatarUpload = async (imageId: string): Promise<AvatarResponse> => {
  const response = await fetch(`${getBaseUrl()}/users/me/avatar`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ imageId }),
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, "Unable to save the avatar."));
  }

  return response.json();
};

export const getApiUser = () => ({
  updateUserIdentity,
  verifyEmailToken,
  createAvatarUploadUrl,
  uploadAvatarToCloudflare,
  completeAvatarUpload,
});

export default getApiUser;
